/* eslint-disable */
// @ts-nocheck
import * as R from 'ramda';
import {
  getAdPartners,
  getAdOntology,
  saveAdPartner,
  disablePartner,
  selectPartner,
  updateConfig,
  savePostback,
  resetSettings,
  deleteInternalPartner,
  rollbackUnsavedChanges,
} from '../../../redux/domains/AdPartner/actions';
import { getErrorCodeMap } from './text-components';
import url from 'url';
import uuid from 'uuid';
import { UNNAMED_PARTNER } from '../../../common/support/adPartnerConstants';
import type {
  AdPartner,
  PostbackParameter,
  ZeitAccountStatus,
  AccountStatus,
  AdAccounts,
  ZeitAccountStatuses,
  CheckedAdAccount,
  AdPartnerWithOnlyDataForSKAN,
  AdAccount,
  Advertiser,
} from '../../types/adPartnerTypes';
import {
  INVALID_TOKEN_STATUS,
  PENDING_REQUEST_STATUS,
  SUCCESS_STATUS,
  PARTNER_ERROR_STATUS,
  DATA_UNAVAILABLE_STATUS,
} from './constants';
import type { Dispatch, RootState } from 'client/js/redux';
import type {
  DeleteInternalPartnerRequestActionPayload,
  DisablePartnerRequestActionPayload,
  GetAdPartnersRequestActionPayload,
  ResetSettingsRequestActionPayload,
  SaveAdPartnerRequestActionPayload,
  SavePostbackRequestActionPayload,
  UpdateConfigRequestActionPayload,
} from '../../../redux/domains/AdPartner/AdPartner.types';

export const mapDispatchProps = (dispatch: Dispatch) => {
  return {
    getAdPartners: (payload: GetAdPartnersRequestActionPayload) => dispatch(getAdPartners(payload)),
    saveAdPartner: (payload: SaveAdPartnerRequestActionPayload) => dispatch(saveAdPartner(payload)),
    disablePartner: (payload: DisablePartnerRequestActionPayload) => dispatch(disablePartner(payload)),
    selectPartner: (payload: { partner: AdPartner | null | undefined }) => dispatch(selectPartner(payload)),
    updateConfig: (payload: UpdateConfigRequestActionPayload) => dispatch(updateConfig(payload)),
    getAdOntology: () => dispatch(getAdOntology()),
    savePostback: (payload: SavePostbackRequestActionPayload) => dispatch(savePostback(payload)),
    resetSettings: (payload: ResetSettingsRequestActionPayload) => dispatch(resetSettings(payload)),
    deleteInternalPartner: (payload: DeleteInternalPartnerRequestActionPayload) => dispatch(deleteInternalPartner(payload)),
    rollbackUnsavedChanges: () => dispatch(rollbackUnsavedChanges()),
  };
};
export const mapStateToProps = (state: RootState) => ({
  user: state.user,
});
export const generateMacroTemplates = (macros: Array<Record<string, any>>): string => {
  if (!macros || !Array.isArray(macros)) {
    return '';
  }

  const template = macros.reduce((queryStr, macro, i) => {
    if (!macro.ad_network_key || !macro.ad_network_template) {
      return queryStr;
    }

    var separator = '&';

    if (i === 0) {
      separator = '';
    }

    var query = `${separator}${macro.ad_network_key}=${macro.ad_network_template}`;

    if (!query) {
      return queryStr;
    }

    return queryStr + query;
  }, '?');

  if (template.length < 2) {
    return '';
  }

  return template;
};
export const generateCredentialQueryStr = (credentials: Array<Record<string, any>>): string => {
  if (!credentials || !Array.isArray(credentials)) {
    return '';
  }

  const queryStr = credentials.reduce((queryStr, credential, i) => {
    if (!credential.ad_network_key || !credential.ad_network_template) {
      return queryStr;
    }

    var query = `&${credential.ad_network_key}=${credential.ad_network_template}`;

    if (!query) {
      return queryStr;
    }

    return queryStr + query;
  }, '');

  if (queryStr.length < 2) {
    return '';
  }

  return queryStr;
};
export const ADS_PATHNAME = '/ads/partner-management';

function buildNotificationString(emptyCredentialsList: Array<string>): string {
  const length = emptyCredentialsList.length;
  return emptyCredentialsList.reduce((acc, curr, i) => {
    var comma = ', ';
    var and = '';

    if (i === 0) {
      comma = '';
      and = '';
    }

    if (length === 2 && i === 1) {
      and = ' and ';
      comma = '';
    }

    if (length > 2 && i === length - 1) {
      and = 'and ';
    }

    acc += `${comma}${and}${curr}`;
    return acc;
  }, '');
}

/*
 * Checks if all credential fields have been entered.
 * If any are empty, return a string for display in warning notification
 *
 * @param {object}  partner
 * @return {string} warning string if all credentials not entered
 */
export const checkCredentials = (partner: Record<string, any>) => {
  const { postback_parameters, credentials } = partner;

  if (!postback_parameters) {
    return null;
  }

  const globalCredentials = postback_parameters.filter((parameter) => parameter.branch_parameter === 'ACCOUNT_CREDENTIAL');
  const missingCredentials = globalCredentials
    .filter((credential) => {
      const credentialName = credential.parameter_name.replace(/\W+/g, '');
      return credential.required && (!credentials || !credentials[credentialName]);
    })
    .map((credential) => credential.dashboard_label);

  if (!missingCredentials.length) {
    return null;
  }

  return buildNotificationString(missingCredentials);
};

/*
 * Returns true if partner has ACCOUNT_CREDENTIAL parameter
 *
 * @param {array} postback_parameters
 * @return {bool}  true if ad partner has credentials param
 */
export const doCredentialsExist = (postback_parameters: Array<PostbackParameter> | null | undefined): boolean => {
  if (!postback_parameters) {
    return false;
  }

  return !!R.find(R.propEq('branch_parameter', 'ACCOUNT_CREDENTIAL'), postback_parameters);
};

/*
 * Returns true if partner has GOAL_ID parameter
 */
export const hasGoalId = (postback_parameters: Array<PostbackParameter> | null | undefined): boolean => {
  if (!postback_parameters) {
    return false;
  }

  return !!R.find(R.propEq('branch_parameter', 'GOAL_ID'), postback_parameters);
};

/*
 * Returns GOAL_ID Dashboard label
 */
export const getDashboardLabel = (postback_parameters: Array<PostbackParameter> | null | undefined): string => {
  if (!postback_parameters || !hasGoalId(postback_parameters)) {
    return 'Goal ID';
  }

  const goal_id_index = R.findIndex(R.propEq('branch_parameter', 'GOAL_ID'), postback_parameters);

  // hasGoalID makes sure the goal index will be above -1
  if (hasGoalId(postback_parameters) && postback_parameters[goal_id_index].dashboard_label) {
    return postback_parameters[goal_id_index].dashboard_label;
  }

  return 'Goal ID';
};
// TODO: DASH-2237 - abstract this to ReduxAsyncAction
export const errorMessageHandler = (error: string | Record<string, any>): any => {
  const errorMessage = R.path(['code'], error) || error;
  const errorCode = R.path([1], errorMessage.match(/\(#(\d+)\)/));
  return (
    getErrorCodeMap(document.location.pathname)[errorCode] || {
      text: error,
      type: 'error',
    }
  );
};
export const getErrorInQuery = (): string | null | undefined => {
  const urlObject = url.parse(window.location.href, true);
  const { query } = urlObject;

  if (!query) {
    return;
  }

  return query.error;
};

/**
 * assumptions about when this method is invoked
 * (1) on an app view
 * (2) the app has a parent organization
 *
 * Indicators of Inheritance (given the above assumptions):
 * - has app id AND org id
 * When partner has never been enabled, we show that the app is not inheriting from the org
 *
 * Nota Bene: In regard to a. the Ad Network Service references the ad_network_org_fallback view in postgres when getting ad networks by app id.
 *  The following cases must be met for the view to return an ad_network record with both an app_id AND organization_id
 *
 * (1) app has a parent org
 * (2) There IS NO app ad network entry
 * (3) There IS an org ad network entry
 */
export const hasInheritedFromOrg = (adPartner: AdPartner | null | undefined): boolean => {
  const { app_id, organization_id } = adPartner || {};
  return !!(app_id && organization_id);
};
export const hasGoogleMachineName = (partner: AdPartner | null | undefined): boolean => {
  return !!(partner && partner.machine_name === 'a_google_adwords');
};
export const getPartnerName = (partner: AdPartner | AdPartnerWithOnlyDataForSKAN | null | undefined): string => {
  if (!partner) {
    return UNNAMED_PARTNER;
  }

  const partnerName = partner.custom_publisher ? partner.publisher_name : partner.company_name;
  return partnerName || UNNAMED_PARTNER;
};
export const parseAccountStatus = (zeitAccountStatus: ZeitAccountStatus): AccountStatus => {
  if (zeitAccountStatus.invalid_credentials) {
    return {
      status: INVALID_TOKEN_STATUS,
      lastSuccessTimestamp: zeitAccountStatus.last_successful_request_timestamp,
    };
  }

  if (zeitAccountStatus.last_request_returned_partner_error) {
    return {
      status: PARTNER_ERROR_STATUS,
      lastSuccessTimestamp: zeitAccountStatus.last_successful_request_timestamp,
    };
  }

  if (!zeitAccountStatus.has_requested_data) {
    return {
      status: PENDING_REQUEST_STATUS,
      lastSuccessTimestamp: zeitAccountStatus.last_successful_request_timestamp,
    };
  }

  if (!zeitAccountStatus.has_data) {
    return {
      status: DATA_UNAVAILABLE_STATUS,
      lastSuccessTimestamp: zeitAccountStatus.last_successful_request_timestamp,
    };
  }

  return {
    status: SUCCESS_STATUS,
    lastSuccessTimestamp: zeitAccountStatus.last_successful_request_timestamp,
  };
};
export const appendStatusToAccounts = (accounts: AdAccounts, zeitAccountStatuses: ZeitAccountStatuses): AdAccounts => {
  return accounts.map((account) => {
    const zeitAccountStatus = zeitAccountStatuses[account.account_id];
    return zeitAccountStatus ? { ...account, accountStatus: parseAccountStatus(zeitAccountStatus) } : account;
  });
};
export const hasMoreThanOneAccount = (accounts: Array<Record<string, any>>): boolean => {
  return accounts?.length > 1;
};
export const moreThanOneAccountSelected = (accounts: CheckedAdAccount[]): boolean => {
  return accounts.filter((a) => a.checked).length > 1;
};
export const isSavedAdAccount = (accountId: string | number, savedAccounts: AdAccounts): boolean => {
  return savedAccounts ? savedAccounts.some((savedAccount) => savedAccount.account_id == accountId) : true;
};

export const isSavedAdvertiser = (accountId: string | number, savedAccounts: Advertiser[]): boolean => {
  return savedAccounts ? savedAccounts.some((savedAccount) => savedAccount.id == accountId) : true;
};

export const convertAdvertiserToAdAccount = (accountId: string | number, savedAccounts: Advertiser[]): boolean => {
  return savedAccounts ? savedAccounts.some((savedAccount) => savedAccount.id == accountId) : true;
};

export const doesAdNetworkRequireAccountIdFromUser = (adNetwork: AdPartner): boolean => {
  const blueprintAccountInfoFields = adNetwork.cost_ingestion_parameters?.account_info.account_info_fields;
  return !!blueprintAccountInfoFields?.find((field) => field.key === 'account_id');
};

export const COST_DATA_AD_ACCOUNT = 'COST_DATA';

export function trimGeneratedAdAccount(partner: AdPartner) {
  const extraData = partner.extra_data;
  const adAccounts = extraData?.ad_accounts;
  if (extraData && adAccounts) {
    extraData.ad_accounts = adAccounts.filter((account) => account.account_name !== COST_DATA_AD_ACCOUNT);
  }
}

export const applyRandomAccountIdToAdNetwork = (adNetwork: AdPartner): AdPartner => {
  let adAccount: AdAccount | undefined = adNetwork.extra_data?.ad_accounts?.find(
    (account) => account.account_name === COST_DATA_AD_ACCOUNT
  );

  if (!adAccount) {
    adAccount = {
      account_name: COST_DATA_AD_ACCOUNT,
      account_id: uuid(),
    };
  } else {
    adAccount.account_id = uuid();
  }

  const extraData = adNetwork.extra_data || { ad_accounts: [] };

  extraData.ad_accounts = extraData.ad_accounts
    ? extraData.ad_accounts.filter((account) => account.account_name !== COST_DATA_AD_ACCOUNT)
    : [];

  extraData.ad_accounts.push(adAccount);

  adNetwork.extra_data = extraData;
  return adNetwork;
};

export const applyTimezoneToAdAccount = (adNetwork: AdPartner, appTimezone?: string): AdPartner => {
  const adAccount: AdAccount | undefined = adNetwork.extra_data?.ad_accounts?.find(
    (account) => account.account_name === COST_DATA_AD_ACCOUNT
  );

  if (!adAccount) {
    return adNetwork;
  }

  const isTimezoneEditable = !!adNetwork.cost_ingestion_parameters?.account_info.timezone_editable;
  const fallbackTimezone = adNetwork.cost_ingestion_parameters?.account_info.default_timezone || 'Etc/GMT';
  adAccount.timezone_name = isTimezoneEditable ? appTimezone : fallbackTimezone;

  const extraData = adNetwork.extra_data || { ad_accounts: [] };

  extraData.ad_accounts = extraData.ad_accounts
    ? extraData.ad_accounts.filter((account) => account.account_name !== COST_DATA_AD_ACCOUNT)
    : [];

  extraData.ad_accounts.push(adAccount);
  adNetwork.extra_data = extraData;

  return adNetwork;
};

export const hasAdNetworkCostDataCredentialsFilled = (adNetwork: AdPartner): boolean => {
  let hasCredentials = false;
  const credentials = adNetwork.cost_ingestion_parameters?.account_info.credential_fields || [];
  const accountInfo = adNetwork.cost_ingestion_parameters?.account_info.account_info_fields || [];
  for (const field of [...credentials, ...accountInfo]) {
    if (adNetwork?.credentials?.[field.key]) {
      hasCredentials = true;
    }
  }

  return hasCredentials;
};
